/**
 * Persist generated binary assets into the scene's resource store.
 *
 * Generated GLBs (meshes, materials) are content-addressed and registered
 * under `/generated/<hash>.<ext>`, which is the key ResourceManager's
 * container loader looks up before it would fetch from an origin — so a
 * Renderer `src` or an object `src` pointing at one resolves from the blob
 * that was just generated, with no upload round-trip.
 *
 * Headless contexts (bench, MCP) also write `public/generated/` so the render
 * harness can serve the file over HTTP.
 */

import type { ToolContext } from '../agent-helpers';

/** Register `blob` as a scene asset and return the src to reference it by. */
export async function persistGeneratedBinary(
    ctx: ToolContext,
    blob: Blob,
    extension: string,
): Promise<string> {
    const bytes = new Uint8Array(await blob.arrayBuffer());
    let hash: string;
    if (typeof process !== 'undefined' && process.versions?.node) {
        const { createHash } = await import('node:crypto');
        hash = createHash('sha256').update(bytes).digest('hex').slice(0, 16);
        const { writeFileSync, mkdirSync } = await import('node:fs');
        mkdirSync('public/generated', { recursive: true });
        writeFileSync(`public/generated/${hash}.${extension}`, bytes);
    } else {
        const digest = await crypto.subtle.digest('SHA-256', bytes.slice().buffer);
        hash = [...new Uint8Array(digest)].map(b => b.toString(16).padStart(2, '0')).join('').slice(0, 16);
    }
    const relSrc = `/generated/${hash}.${extension}`;
    ctx.traceScene.resources.addAsset(relSrc, blob);
    return relSrc;
}
